// ============================================================
// MANIC MEETER — Events: selection logic
// ============================================================
// The content itself lives in src/i18n/<locale>/events.ts. This module only
// decides *which* piece of it the player runs into, so a language switch needs
// no more than the next call to C().

import {
  BrainstormingChallenge, BuzzwordBingoChallenge, CodeReviewChallenge, ColleagueRole,
  EmailSortierenChallenge, KalenderTetrisChallenge, KiHalluzinationChallenge,
  Question, RandomEncounter, SimonSaysChallenge, SpecialEventType,
} from "./types";
import { getNeonClass } from "./renderer";
import { C } from "./i18n";
import { colleagueRoleLabel } from "./content";
import type { AddressMethod, ProcrastinationActivity } from "./i18n/contract";

export type { AddressMethod, ProcrastinationActivity };

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ---- Content accessors (always the active locale) ----

export function procrastinationActivities(): ProcrastinationActivity[] {
  return C().events.procrastination;
}

export function meetingFlavorLines(): string[] {
  return C().events.meetingFlavor;
}

export function randomEncounters(): RandomEncounter[] {
  return C().events.encounters;
}

// ---- Rolls ----

export function rollSpecialEvent(): SpecialEventType {
  const roll = Math.random();
  if (roll < 0.15) return SpecialEventType.Question;
  if (roll < 0.24) return SpecialEventType.Brainstorming;
  if (roll < 0.33) return SpecialEventType.SimonSays;
  if (roll < 0.41) return SpecialEventType.BuzzwordBingo;
  if (roll < 0.48) return SpecialEventType.EmailSortieren;
  if (roll < 0.54) return SpecialEventType.KalenderTetris;
  if (roll < 0.62) return SpecialEventType.CodeReview;
  if (roll < 0.70) return SpecialEventType.KiHalluzination;
  return SpecialEventType.None;
}

export function getRandomQuestion(): Question {
  return pick(C().events.meetingQuestions);
}

export function getRandomBossQuestion(): Question {
  return pick(C().events.bossQuestions);
}

export function getRandomBrainstorming(): BrainstormingChallenge {
  return pick(C().events.brainstorming);
}

export function getRandomSimonSays(): SimonSaysChallenge {
  return pick(C().events.simonSays);
}

export function getRandomBuzzwordBingo(): BuzzwordBingoChallenge {
  return pick(C().events.buzzwordBingo);
}

export function getRandomEmailSortieren(): EmailSortierenChallenge {
  return pick(C().events.emailSortieren);
}

export function getRandomKalenderTetris(): KalenderTetrisChallenge {
  return pick(C().events.kalenderTetris);
}

export function getRandomCodeReview(): CodeReviewChallenge {
  return pick(C().events.codeReview);
}

export function getRandomKiHalluzination(): KiHalluzinationChallenge {
  return pick(C().events.kiHalluzination);
}

export function getRandomEncounter(): RandomEncounter {
  return pick(C().events.encounters);
}

export function getRandomFlavor(): string {
  return pick(C().events.meetingFlavor);
}

export function getDirectionChangeQuote(): string {
  return pick(C().events.directionChange);
}

// ---- Address phrases: subtle hint that the player was addressed ----

export function generateAddressPhrase(
  playerName: string,
  playerRole: string,
  method: AddressMethod,
  colleagueName: string,
  colleagueRole: ColleagueRole,
): string {
  const neonCls = getNeonClass(colleagueRole);
  const speaker = `💬 <span class="speaker-name ${neonCls}">${colleagueName}</span> <span class="role-tag">${colleagueRoleLabel(colleagueRole)}</span>`;
  const hint = (text: string) => `<span class="address-hint">${text}</span>`;
  const quote = pick(C().events.addressPhrases({ playerName, playerRole, hint })[method]);
  return `${speaker}: <span class="${neonCls}">${quote}</span>`;
}
